import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowUp } from "lucide-react";
import { RazorInputField } from "../../../../components/razor-reuse";
import type { AppDispatch } from "../../../../redux/store";
import RootState from "../../../../redux/type";
import {
  exportRateComparisonCsv,
  getComparisonCurrencyPairOptions,
  getRateComparison,
} from "../../../../redux/rateIntelligence";
import { COMPARISON_PROVIDER_ORDER } from "../types";
import RateComparisonTable from "./RateComparisonTable";

const ALL_PAIRS_OPTION = { label: "All pairs", value: "" };

const POSITION_OPTIONS = [
  { label: "All positions", value: "" },
  { label: "Leading", value: "leading" },
  { label: "Behind", value: "behind" },
];

export default function ComparisonTab() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const canExport = !!user?.permissions?.includes("export_rate_comparison");
  const {
    comparison_pairs,
    comparison_quoted_at,
    comparison_meta,
    comparison_currency_pair_options,
    loading_comparison,
  } = useSelector((state: RootState) => state.rate_intelligence);

  const currencyPairOptions = useMemo(
    () => [ALL_PAIRS_OPTION, ...(comparison_currency_pair_options || [])],
    [comparison_currency_pair_options],
  );

  const [currencyPair, setCurrencyPair] = useState(ALL_PAIRS_OPTION);
  const [position, setPosition] = useState(POSITION_OPTIONS[0]);
  const [page, setPage] = useState(1);

  // Populates the dropdown from every page of the live dataset, independent
  // of the single filtered/paginated page fetched below.
  useEffect(() => {
    dispatch(getComparisonCurrencyPairOptions() as any);
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getRateComparison({
        currency_pair: currencyPair.value,
        position: position.value as any,
        page,
      }) as any,
    );
  }, [dispatch, currencyPair, position, page]);

  const handleExport = () => {
    dispatch(
      exportRateComparisonCsv({
        currency_pair: currencyPair.value,
        position: position.value as any,
        fileLabel: "rate comparison report",
      }) as any,
    );
  };

  return (
    <div className="mt-[16px]">
      <div className="flex items-end justify-between gap-[16px] flex-wrap">
        <div className="flex items-end gap-[16px] flex-wrap">
          <div className="w-[200px]">
            <RazorInputField
              color="black-light"
              type="select"
              label="Currency pair"
              value={currencyPair}
              onChange={(e: any) => {
                setCurrencyPair(e);
                setPage(1);
              }}
              selectOption={currencyPairOptions}
            />
          </div>
          <div className="w-[200px]">
            <RazorInputField
              color="black-light"
              type="select"
              label="Position"
              value={position}
              onChange={(e: any) => {
                setPosition(e);
                setPage(1);
              }}
              selectOption={POSITION_OPTIONS}
            />
          </div>
        </div>
        {canExport && (
          <button
            type="button"
            onClick={handleExport}
            className="flex h-[40px] items-center gap-[8px] rounded-[8px] border border-[#E4E4E7] px-[16px] text-[14px] text-[#1E1E1E] hover:bg-[#F7F7F7]"
          >
            <ArrowUp className="size-[14px]" />
            Export
          </button>
        )}
      </div>

      <div className="mt-[16px]">
        <RateComparisonTable
          pairs={comparison_pairs || []}
          providers={COMPARISON_PROVIDER_ORDER}
          quotedAt={comparison_quoted_at}
          loading={loading_comparison}
          currentPage={comparison_meta?.current_page || page}
          perPage={comparison_meta?.per_page || 10}
          totalCount={comparison_meta?.total || 0}
          onPageChange={(num) => setPage(num)}
        />
      </div>
    </div>
  );
}
